<template>
  <div class="view-container">
    <div class="search-panel">
      <div class="search-row">
        <div class="search-item">
          <label><i class="fas fa-search"></i> 流程/代理人</label>
          <input v-model="searchKeyword" type="text" placeholder="输入流程名称或代理人..." class="search-input">
        </div>
        <div class="search-item">
          <label><i class="fas fa-filter"></i> 状态</label>
          <select v-model="statusFilter" class="search-select">
            <option value="">全部状态</option>
            <option value="active">生效中</option>
            <option value="pending">待生效</option>
            <option value="expired">已过期</option>
          </select>
        </div>
        <div class="search-actions">
          <button @click="searchKeyword = ''; statusFilter = ''" class="btn btn-default search-btn">
            <i class="fas fa-undo"></i> 重置
          </button>
        </div>
      </div>
    </div>

    <div class="stats-row">
      <div class="stat-card">
        <div class="stat-num">{{ activeCount }}</div>
        <div class="stat-label">生效中</div>
      </div>
      <div class="stat-card">
        <div class="stat-num">{{ pendingCount }}</div>
        <div class="stat-label">待生效</div>
      </div>
      <div class="stat-card">
        <div class="stat-num">{{ expiredCount }}</div>
        <div class="stat-label">已过期</div>
      </div>
    </div>

    <div class="table-wrapper">
      <div class="table-header-row">
        <button @click="openAddDialog" class="btn btn-primary">
          <i class="fas fa-plus"></i> 新增代理
        </button>
      </div>
      <div class="table-container">
        <table class="data-table">
          <thead>
            <tr>
              <th>流程名称</th>
              <th>代理委托人</th>
              <th>被代理人</th>
              <th>开始时间</th>
              <th>结束时间</th>
              <th width="80">状态</th>
              <th>备注</th>
              <th width="100">操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="proxy in filteredProxies" :key="proxy.guid">
              <td class="t-process-name">{{ proxy.processName }}</td>
              <td>{{ proxy.principal }}</td>
              <td>{{ proxy.agentName }}</td>
              <td>{{ proxy.startDate || '-' }}</td>
              <td>{{ proxy.endDate || '-' }}</td>
              <td>
                <span class="proxy-status" :class="proxy.status">
                  {{ getStatusLabel(proxy.status) }}
                </span>
              </td>
              <td class="t-remark">{{ proxy.remark || '-' }}</td>
              <td class="action-column">
                <button class="btn btn-primary btn-xs" title="修改" @click="openEditDialog(proxy)">
                  <i class="fas fa-edit"></i>
                </button>
                <button class="btn btn-danger btn-xs" title="删除" @click="handleDelete(proxy)">
                  <i class="fas fa-trash"></i>
                </button>
              </td>
            </tr>
            <tr v-if="filteredProxies.length === 0">
              <td colspan="8" class="empty-cell">暂无代理记录，点击上方按钮新增</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div v-if="showModal" class="modal-overlay" @click.self="closeModal">
      <div class="modal-dialog">
        <div class="modal-header">
          <h4>{{ formTitle }}</h4>
          <button @click="closeModal" class="modal-close">&times;</button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label>代理流程 *</label>
            <select v-model="proxyForm.processGuid" class="form-control">
              <option value="">请选择流程</option>
              <option v-for="p in processOptions" :key="p.guid" :value="p.guid">{{ p.processName }}</option>
            </select>
          </div>
          <div class="form-row">
            <div class="form-group">
              <label>代理委托人 *</label>
              <input v-model="proxyForm.principal" type="text" class="form-control" disabled>
              <span class="form-hint">默认为当前登录用户</span>
            </div>
            <div class="form-group">
              <label>被代理人 *</label>
              <select v-model="proxyForm.agentAccount" class="form-control">
                <option value="">请选择被代理人</option>
                <option v-for="u in allUsers" :key="u.account" :value="u.account">{{ u.displayName }} ({{ u.account }})</option>
              </select>
            </div>
          </div>
          <div class="form-row">
            <div class="form-group">
              <label>开始时间 *</label>
              <input v-model="proxyForm.startDate" type="date" class="form-control">
            </div>
            <div class="form-group">
              <label>结束时间 *</label>
              <input v-model="proxyForm.endDate" type="date" class="form-control">
            </div>
          </div>
          <div class="form-group">
            <label>备注</label>
            <textarea v-model="proxyForm.remark" class="form-control" rows="2" placeholder="代理原因等备注信息"></textarea>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="closeModal" class="btn btn-default">取消</button>
          <button @click="saveProxy" class="btn btn-primary" :disabled="saving">{{ saving ? '保存中...' : '确认保存' }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, reactive, onMounted } from 'vue'
import { getProfile } from '../api/auth'
import { getUserList } from '../api/user'
import { getAgentRules, saveAgentRule, deleteAgentRule, getAvailableProcesses } from '../api/userSetting'

const searchKeyword = ref('')
const statusFilter = ref('')
const saving = ref(false)

const proxies = ref([])
const allUsers = ref([])
const processOptions = ref([])
const currentUser = reactive({ account: '', displayName: '' })

const showModal = ref(false)
const editingGuid = ref('')

const proxyForm = reactive({
  processGuid: '',
  principal: '',
  agentAccount: '',
  agentName: '',
  startDate: '',
  endDate: '',
  remark: ''
})

const today = new Date().toISOString().split('T')[0]

const activeCount = computed(() => proxies.value.filter(p => p.status === 'active').length)
const pendingCount = computed(() => proxies.value.filter(p => p.status === 'pending').length)
const expiredCount = computed(() => proxies.value.filter(p => p.status === 'expired').length)

const filteredProxies = computed(() => {
  return proxies.value.filter(p => {
    const matchKeyword = !searchKeyword.value ||
      (p.processName || '').toLowerCase().includes(searchKeyword.value.toLowerCase()) ||
      (p.agentName || '').toLowerCase().includes(searchKeyword.value.toLowerCase())
    const matchStatus = !statusFilter.value || p.status === statusFilter.value
    return matchKeyword && matchStatus
  }).sort((a, b) => {
    if (a.status === 'active' && b.status !== 'active') return -1
    if (a.status !== 'active' && b.status === 'active') return 1
    if (a.status === 'pending' && b.status === 'expired') return -1
    if (a.status === 'expired' && b.status === 'pending') return 1
    return 0
  })
})

const formTitle = computed(() => editingGuid.value ? '修改代理设置' : '新增代理设置')

function getStatusLabel(status) {
  const map = { active: '生效中', pending: '待生效', expired: '已过期' }
  return map[status] || status
}

// ==================== 数据加载 ====================
const loadData = async () => {
  try {
    const prof = await getProfile()
    const pd = prof.data || prof.Data || {}
    currentUser.account = pd.account || pd.Account || ''
    currentUser.displayName = pd.displayName || pd.DisplayName || ''

    const r = await getAgentRules(1)
    proxies.value = (r.data || r.Data || []).map(d => ({
      guid: d.guid || d.Guid,
      processName: d.processName || d.ProcessName,
      principal: d.principal || d.Principal,
      agentName: d.agentName || d.AgentName,
      agentAccount: d.agentAccount || d.AgentAccount,
      startDate: d.startDate || d.StartDate,
      endDate: d.endDate || d.EndDate,
      status: d.status || d.Status || 'active',
      remark: d.remark || d.Remark,
      flowGuids: d.flowGuids || d.FlowGuids
    }))
  } catch (e) { console.error('加载代理规则失败', e) }
}

const loadUsers = async () => {
  try {
    const res = await getUserList()
    allUsers.value = (res.data || res.Data || []).map(u => ({
      account: u.account || u.Account,
      displayName: u.displayName || u.DisplayName
    }))
  } catch (e) { console.error('加载用户失败', e) }
}

const loadProcesses = async (excludeGuid) => {
  try {
    const res = await getAvailableProcesses(1, excludeGuid || '')
    processOptions.value = (res.data || res.Data || []).map(p => ({
      guid: p.guid,
      processName: p.processName
    }))
  } catch (e) { console.error('加载流程失败', e) }
}

// ==================== 新增/修改 ====================
function openAddDialog() {
  editingGuid.value = ''
  proxyForm.processGuid = ''
  proxyForm.principal = currentUser.displayName
  proxyForm.agentAccount = ''
  proxyForm.agentName = ''
  proxyForm.startDate = ''
  proxyForm.endDate = ''
  proxyForm.remark = ''
  loadProcesses('')
  showModal.value = true
}

function openEditDialog(proxy) {
  editingGuid.value = proxy.guid
  proxyForm.processGuid = (proxy.flowGuids || '').split(';')[0] || ''
  proxyForm.principal = proxy.principal
  proxyForm.agentAccount = proxy.agentAccount
  proxyForm.agentName = proxy.agentName
  proxyForm.startDate = proxy.startDate || ''
  proxyForm.endDate = proxy.endDate || ''
  proxyForm.remark = proxy.remark || ''
  loadProcesses(proxy.guid)
  showModal.value = true
}

function closeModal() { showModal.value = false }

async function saveProxy() {
  if (!proxyForm.processGuid) { $message.warning('请选择代理流程'); return }
  if (!proxyForm.agentAccount) { $message.warning('请选择被代理人'); return }
  if (!proxyForm.startDate || !proxyForm.endDate) { $message.warning('请选择代理时间范围'); return }
  if (proxyForm.startDate > proxyForm.endDate) { $message.warning('开始时间不能晚于结束时间'); return }

  const selectedUser = allUsers.value.find(u => u.account === proxyForm.agentAccount)
  saving.value = true
  try {
    const payload = {
      ruleType: 1,
      agentAccount: proxyForm.agentAccount,
      agentName: selectedUser?.displayName || proxyForm.agentAccount,
      principal: currentUser.account,
      flowGuids: [proxyForm.processGuid],
      startDate: proxyForm.startDate,
      endDate: proxyForm.endDate,
      remark: proxyForm.remark,
      type: editingGuid.value ? 'Edit' : 'Add'
    }
    if (editingGuid.value) payload.ruleGuid = editingGuid.value

    const res = await saveAgentRule(payload)
    if (res.resultType || res.ResultType) {
      closeModal()
      await loadData()
    } else {
      $message.error(res.message || res.Message || '保存失败')
    }
  } catch (e) { $message.error('保存失败') }
  finally { saving.value = false }
}

async function handleDelete(proxy) {
  if (!await $confirm(`确定删除 "${proxy.processName}" 的代理设置吗？`)) return
  try {
    await deleteAgentRule(proxy.guid)
    await loadData()
  } catch (e) { $message.error('删除失败') }
}

onMounted(() => {
  loadData()
  loadUsers()
})
</script>

<style scoped>
.view-container {
  padding: 20px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 150px);
  overflow: hidden;
  margin: 16px;
}

.search-panel {
  background: #fff;
  border: 1px solid #e8ecf1;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 10px;
  flex-shrink: 0;
}

.search-row {
  display: flex;
  gap: 14px;
  align-items: flex-end;
}

.search-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.search-item label {
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  display: flex;
  align-items: center;
  gap: 4px;
}

.search-item label i {
  font-size: 11px;
  color: #9ca3af;
}

.search-input, .search-select {
  padding: 6px 10px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 12px;
  outline: none;
  background: #fff;
  transition: border-color .15s, box-shadow .15s;
  min-width: 160px;
}

.search-input:focus, .search-select:focus {
  border-color: #10b981;
  box-shadow: 0 0 0 2px rgba(16,185,129,.1);
}

.search-actions {
  display: flex;
  gap: 8px;
  align-items: flex-end;
  flex-shrink: 0;
}

.search-btn {
  padding: 6px 12px;
}

.stats-row {
  display: flex;
  gap: 12px;
  margin-bottom: 15px;
  flex-shrink: 0;
}

.stat-card {
  flex: 1;
  text-align: center;
  padding: 12px;
  background: #f9fafb;
  border-radius: 6px;
  border: 1px solid #e8ecf1;
}

.stat-num {
  font-size: 22px;
  font-weight: 700;
  color: #1f2937;
}

.stat-label {
  font-size: 11px;
  color: #9ca3af;
  margin-top: 2px;
}

.table-wrapper {
  border: 1px solid #e7eaec;
  border-radius: 4px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}

.table-header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 15px;
  background-color: #f9f9f9;
  border-bottom: 1px solid #e7eaec;
  flex-shrink: 0;
}

.table-container {
  overflow-y: auto;
  flex: 1;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
}

.data-table thead th {
  padding: 10px 12px;
  text-align: left;
  font-size: 11px;
  font-weight: 600;
  color: #6b7280;
  background: #f8fafc;
  border-bottom: 2px solid #e7eaec;
  white-space: nowrap;
  position: sticky;
  top: 0;
  z-index: 1;
}

.data-table tbody td {
  padding: 9px 12px;
  border-bottom: 1px solid #f3f4f6;
  font-size: 12px;
  color: #374151;
}

.data-table tbody tr:hover {
  background: #f9fafb;
}

.t-process-name {
  font-weight: 500;
  color: #1f2937;
}

.t-remark {
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #9ca3af;
}

.proxy-status {
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 500;
}

.proxy-status.active { background: #d1fae5; color: #059669; }
.proxy-status.pending { background: #fef3c7; color: #d97706; }
.proxy-status.expired { background: #fee2e2; color: #ef4444; }

.action-column {
  display: flex;
  gap: 6px;
}

.empty-cell {
  text-align: center;
  padding: 40px 16px;
  color: #9ca3af;
  font-size: 13px;
}

.btn {
  padding: 6px 14px;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 5px;
}

.btn-primary {
  background: #10b981;
  border: none;
  color: #fff;
}
.btn-primary:hover:not(:disabled) { background: #059669; }
.btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }

.btn-default {
  background: #fff;
  border: 1px solid #d1d5db;
  color: #374151;
}
.btn-default:hover { background: #f9fafb; }

.btn-xs {
  padding: 4px 10px;
  font-size: 11px;
  border-radius: 3px;
}

.btn-danger {
  background: #fff;
  border: 1px solid #fecaca;
  color: #ef4444;
}
.btn-danger:hover { background: #ef4444; color: #fff; }

.form-control {
  padding: 7px 10px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 12px;
  outline: none;
}
.form-control:focus { border-color: #10b981; }

.modal-overlay {
  position: fixed; inset: 0;
  background: rgba(0,0,0,0.4);
  display: flex; align-items: center; justify-content: center;
  z-index: 1000;
}

.modal-dialog {
  background: #fff;
  border-radius: 6px;
  width: 520px;
  max-width: 90vw;
}

.modal-header {
  display: flex; justify-content: space-between; align-items: center;
  padding: 12px 14px;
  border-bottom: 1px solid #e8ecf1;
}
.modal-header h4 { margin: 0; font-size: 13px; font-weight: 600; color: #1f2937; }
.modal-close { width: 24px; height: 24px; border: none; background: none; font-size: 18px; color: #9ca3af; cursor: pointer; }

.modal-body { padding: 14px; }

.form-row { display: flex; gap: 12px; }
.form-row .form-group { flex: 1; }

.form-group { margin-bottom: 12px; }
.form-group label { display: block; font-size: 11px; font-weight: 500; color: #374151; margin-bottom: 4px; }
.form-group .form-control { width: 100%; }

.form-hint { display: block; font-size: 10px; color: #9ca3af; margin-top: 3px; }

.modal-footer {
  display: flex; justify-content: flex-end; gap: 6px;
  padding: 10px 14px;
  border-top: 1px solid #e8ecf1;
}
</style>
